import { defaults, findIndex, map, sum } from "lodash";

import type {
  CheckoutStep,
  ConfirmationSource
} from "@app/types/checkout-types";
import type { CurrencyCode } from "@app/types/currency";
import type { GeoDataCountry } from "@app/types/geo-data";
import type { Nullable } from "@app/types/meta-types";
import type {
  BookingPassenger,
  CartPassenger,
  CreditCardBrand,
  NapiOperator,
  NapiUser,
  PaymentType
} from "@app/types/napi-types";
import type {
  Ancillary,
  AncillaryCategory,
  BusbudProductType
} from "@app/types/napi-v2-ancillary";
import { isHepstarAncillary } from "@app/types/napi-v2-ancillary";
import type { NapiV2TripItem } from "@app/types/napi-v2-cart-types";
import type { NapiV2CartCharges } from "@app/types/napi-v2-charges-types";
import type {
  TrackingAnalytics,
  TrackingPage,
  TrackingUser
} from "@app/types/tracking";
import type {
  CancelledPriceUpdate,
  CheckoutErrorPopup,
  ClickedCheckoutSeat,
  ClickedCheckoutStep,
  ClickedCreateAccountToggle,
  ClickedEmailOptInCheckbox,
  ClickedEmailSuggestion,
  ClickedRedeemCredits,
  ClickedReferralCartSelect,
  ClickedUnredeemCredits,
  ClickedUpdateCurrency,
  ClickedUpdatePrice,
  CompletedOrder,
  ConfirmedCheckout,
  DiscountCodeError,
  EnteredCheckoutPayment,
  ExitedCheckoutSeats,
  FailedCheckoutForm,
  FailedCheckoutPassengerForm,
  FailedOrder,
  NonAlphabeticNameWarningTriggered,
  OpenedCheckoutSeats,
  OpenedCountryPicker,
  OpenedInstallmentsField,
  OpenedRefundProtectDetails,
  PickedCountryCodeInPicker,
  PickedCountryInPicker,
  PricedQuestionsInfo,
  RedeemCreditsError,
  Redirect3DS,
  SelectedAncillary,
  SelectedSavedTraveller,
  StartedCheckout,
  UnredeemCreditsError,
  UpdatedInstallmentsQuantity,
  UnselectedAncillary,
  SavePassengerQuestionAnswersCheckboxToggled,
  CheckoutAddonsViewed,
  CheckoutAddonsSelected,
  CheckoutPaymentFormViewed,
  CheckoutPaymentFormSubmitted
} from "@app/types/tracking/checkout";
import type { AmplitudeEvent } from "@app/types/tracking/common";

import * as PropertyValues from "./constants/property-values";
import { CheckoutTrackingAction } from "./constants/tracking-action";
import { buildAddPaymentInfoEvent } from "./ga4/event";

/**
 * Possible values from "X-UA-Device" request header set by Fastly
 *
 */
export const CheckoutTrackingSteps = {
  STARTED_CHECKOUT: "started_checkout",
  ENTERED_PASSENGERS: "entered_passengers",
  ENTERED_SEATS: "entered_seats",
  ENTERED_PAYMENT: "entered_payment"
} as const;

export function startedCheckout(
  analytics: TrackingAnalytics
): AmplitudeEvent<StartedCheckout> {
  const properties = analytics.page.details;

  return {
    action: CheckoutTrackingAction.StartedCheckout,
    category: "checkout",
    label: properties.cart_id,
    details: properties
  };
}

export function clickedEmailSuggestion(
  analytics: TrackingAnalytics
): AmplitudeEvent<ClickedEmailSuggestion> {
  const properties = analytics.page.details;

  return {
    action: CheckoutTrackingAction.ClickedEmailSuggestion,
    category: "checkout",
    label: properties.cart_id,
    details: properties
  };
}

export function enteredCheckoutPayment(
  analytics: TrackingAnalytics
): AmplitudeEvent<EnteredCheckoutPayment> {
  const properties = analytics.page.details;

  return {
    action: CheckoutTrackingAction.EnteredCheckoutPayment,
    category: "checkout",
    label: properties.cart_id,
    details: properties,
    ecommerce: { checkout: { actionField: { step: 3 } } }
  };
}

export function clickedCheckoutStep({
  new_step,
  analytics,
  opt_in_save_passenger_question_answers,
  prefilled_questions_per_passenger = [],
  current_step
}: {
  new_step: string;
  analytics: TrackingAnalytics;
  opt_in_save_passenger_question_answers: boolean;
  prefilled_questions_per_passenger?: string[][];
  current_step?: string;
}): AmplitudeEvent<ClickedCheckoutStep> {
  const properties = defaults(
    {
      checkout_new_step: new_step,
      opt_in_save_passenger_question_answers,
      prefilled_questions_per_passenger:
        prefilled_questions_per_passenger.reduce(
          (acc: string[], passenger: string[], passenger_index: number) => {
            acc.push(
              ...passenger.map(
                question_key => `${question_key}_${passenger_index}`
              )
            );
            return acc;
          },
          []
        ),
      has_prefilled_questions_per_passenger:
        prefilled_questions_per_passenger.map(
          passenger_question_keys => passenger_question_keys.length > 0
        ),
      ...(current_step && { checkout_step: current_step })
    },
    analytics.page.details
  );

  return {
    action: CheckoutTrackingAction.ClickedCheckoutStep,
    category: "checkout",
    label: properties.cart_id,
    details: properties,
    gtm_props: {
      checkout_step: properties.checkout_step
    }
  };
}

export function failedCheckoutPassengerForm(
  analytics: TrackingAnalytics,
  form_errors: string[] = []
): AmplitudeEvent<FailedCheckoutPassengerForm> {
  const properties = analytics.page.details;

  return {
    action: CheckoutTrackingAction.FailedCheckoutPassengerForm,
    category: "checkout",
    label: properties.cart_id,
    details: { ...properties, passenger_form_errors: form_errors }
  };
}

interface ConfirmedCheckoutParams {
  passengers?: Array<CartPassenger | BookingPassenger>;
  payment_type: PaymentType;
  credit_card_brand?: CreditCardBrand | string | null;
  installments_quantity?: number;
  analytics: TrackingAnalytics;
  charges: NapiV2CartCharges | undefined;
  billing_country?: string | null;
  priced_questions_info?: PricedQuestionsInfo | null;
  cart_id?: string;
  checkout_step?: CheckoutStep;
}

export function confirmedCheckout({
  checkout_step = "payment",
  passengers,
  payment_type,
  credit_card_brand = PropertyValues.NA_STRING,
  installments_quantity = 1,
  analytics,
  charges,
  billing_country,
  priced_questions_info,
  cart_id
}: ConfirmedCheckoutParams): AmplitudeEvent<ConfirmedCheckout> {
  let properties = Object.assign({}, analytics.page.details);

  const primary_passenger_index = passengers
    ? findIndex(passengers, { primary_contact: true }) + 1
    : PropertyValues.NA_STRING;

  const busbud_loyalty_credits_amount =
    charges?.items.find(item => item.type === "loyalty_credit_redeem")
      ?.amount || 0;
  const busbud_credits_amount =
    charges?.items.find(item => item.type === "cash_credit_redeem")?.amount ||
    0 + busbud_loyalty_credits_amount;

  const total_amount = charges?.total || 0;

  properties = Object.assign(properties, {
    checkout_step,
    payment_type: payment_type,
    payment_card_type: credit_card_brand,
    primary_passenger: primary_passenger_index,
    installments_quantity: installments_quantity,
    billing_country: billing_country,
    cart_id: cart_id,
    busbud_credits_amount,
    busbud_loyalty_credits_amount,
    total_amount,
    ...priced_questions_info
  });

  return {
    action: CheckoutTrackingAction.ConfirmedCheckout,
    category: "checkout",
    label: properties.cart_id,
    details: properties,
    gtm_props: buildAddPaymentInfoEvent(analytics.section_properties)
  };
}

export function redirectThreeDS(
  analytics: TrackingAnalytics
): AmplitudeEvent<Redirect3DS> {
  const properties = analytics.page.details;

  return {
    action: CheckoutTrackingAction.Redirect3DS,
    category: "checkout",
    label: properties.cart_id,
    details: properties
  };
}

/**
 * @param errors Unique IDs of inputs containing errors
 * @param analytics
 * @returns
 */
export function failedCheckoutForm(
  errors: string[],
  analytics: TrackingAnalytics
): AmplitudeEvent<FailedCheckoutForm> {
  const details = Object.assign(
    {
      checkout_errors_count: errors.length,
      checkout_errors: errors
    },
    analytics.page.details
  );

  return {
    action: CheckoutTrackingAction.FailedCheckoutForm,
    category: "checkout",
    label: details.cart_id,
    details: details
  };
}

interface CheckoutErrorDetails {
  is_update_price_flow?: boolean | undefined;
  new_price?: number | undefined;
  old_price?: number | undefined;
  is_currency_change_flow?: boolean | undefined;
  old_currency?: string | undefined;
  suggested_currency?: string | undefined;
  root_error_name?: string | undefined;
}
export function checkoutErrorPopup(
  purchase_id: number | null,
  error_name: string | null,
  error_details: CheckoutErrorDetails | null,
  analytics: TrackingAnalytics
): AmplitudeEvent<CheckoutErrorPopup> {
  const properties = Object.assign(
    {
      /** GTM Variable "event purchase_id" */
      purchase_id: purchase_id || PropertyValues.EMPTY_NUMBER,
      checkout_error_name: error_name
    },
    analytics.page.details
  );

  if (error_details) {
    const {
      is_update_price_flow,
      new_price,
      old_price,
      is_currency_change_flow,
      old_currency,
      suggested_currency,
      root_error_name
    } = error_details;

    properties.root_error_name = root_error_name
      ? root_error_name
      : PropertyValues.NA_STRING;

    if (is_update_price_flow && new_price && old_price) {
      properties.checkout_is_update_price_flow = true;
      properties.checkout_price_variation = new_price - old_price;
      properties.checkout_price_variation_pc = Math.floor(
        (properties.price_variation / old_price) * 100
      );
    }

    if (is_currency_change_flow && old_currency && suggested_currency) {
      properties.checkout_is_currency_change_flow = true;
      properties.checkout_old_currency = old_currency;
      properties.checkout_suggested_currency = suggested_currency;
      properties.checkout_currency_change = `${old_currency}-${suggested_currency}`;
    }
  }

  return {
    action: CheckoutTrackingAction.CheckoutErrorPopup,
    category: "checkout",
    label: properties.cart_id,
    details: properties
  };
}

type BillingInfo = {
  address: string;
  country: string;
  city: string;
  state: string;
  zip: string;
};

export function completedOrder(
  purchase_id: number | null,
  user: Pick<
    NapiUser,
    "email" | "phone_number" | "first_name" | "last_name"
  > | null,
  billing_info: BillingInfo,
  num_tickets: number,
  purchase_type: string,
  cart_items: Ancillary[] | null,
  save_billing_address_checked: boolean,
  billing_address_changed: boolean,
  partner_currency: string,
  gmv_in_partner_currency: number,
  revenue_in_partner_currency: number,
  gmv_in_cad: number,
  revenue_in_cad: number,
  analytics: TrackingAnalytics,
  checkout_step: CheckoutStep = "payment",
  payment_type: PaymentType,
  trip_item: NapiV2TripItem | undefined,
  cart_charges: NapiV2CartCharges,
  priced_questions_info?: PricedQuestionsInfo | null
): AmplitudeEvent<CompletedOrder> {
  const checkout_ancillary_products_explicit_decline_list: string[] = [];
  const checkout_ancillary_products_selected_list = (cart_items || [])
    .filter(item => {
      if (item.status === "SELECTED") {
        return item;
      } else {
        if ("partner_data" in item) {
          checkout_ancillary_products_explicit_decline_list.push(
            item.partner_data.product_id ||
              item.partner_data.label ||
              item.category
          );
        } else {
          checkout_ancillary_products_explicit_decline_list.push(
            item.product_id || item.category
          );
        }
        return;
      }
    })
    .map(item => {
      if ("partner_data" in item) {
        return item.partner_data.product_id || item.partner_data.label;
      } else {
        return item.category;
      }
    });
  const properties = Object.assign(
    {
      /** GTM Variable "event purchase_id" */
      checkout_step,
      payment_type,
      purchase_id: purchase_id,
      checkout_save_billing_address_checked: save_billing_address_checked,
      checkout_billing_address_changed: billing_address_changed,
      checkout_billing_country: billing_info.country,
      checkout_billing_city: billing_info.city,
      checkout_billing_state: billing_info.state,
      checkout_num_tickets: num_tickets,
      checkout_purchase_type: purchase_type,
      checkout_ancillary_products_explicit_decline_list,
      checkout_ancillary_products_explicit_decline_count:
        checkout_ancillary_products_explicit_decline_list.length,
      checkout_ancillary_products_selected_count:
        checkout_ancillary_products_selected_list.length,
      checkout_ancillary_products_selected_list,
      $revenue: revenue_in_cad && revenue_in_cad / 100,
      ...priced_questions_info
    },
    analytics.page.details
  );

  const { purchase_tax, purchase_shipping } = getCheckoutData(analytics);

  return {
    action: CheckoutTrackingAction.CompletedOrder,
    category: "checkout",
    label: properties.purchase_id,
    details: properties,
    trigger: "purchase",
    gtm_props: {
      email: user?.email ?? "",
      phone: user?.phone_number ?? "",
      first_name: user?.first_name ?? "",
      last_name: user?.last_name ?? "",
      billing_street: billing_info.address,
      billing_city: billing_info.city,
      billing_region: billing_info.state,
      billing_country: billing_info.country,
      billing_postal_code: billing_info.zip,
      gmv_in_cad: gmv_in_cad / 100,
      revenue_in_cad: revenue_in_cad / 100,
      partner_currency,
      gmv_in_partner_currency: gmv_in_partner_currency / 100,
      revenue_in_partner_currency: revenue_in_partner_currency / 100,
      purchase_id: properties.purchase_id,
      purchase_item_name: trip_item?.segments[0]?.operator.name || "unknown",
      purchase_ticket_count: num_tickets,
      purchase_charges_subtotal: cart_charges.subtotal,
      purchase_charges_currency: cart_charges.currency
    },

    ecommerce: {
      purchase: {
        actionField: {
          id: properties.purchase_id,
          tax: purchase_tax,
          shipping: purchase_shipping
        }
      }
    }
  };
}
const getCheckoutData = (analytics: TrackingAnalytics) => {
  const base_values = {
    purchase_tax: 0,
    purchase_shipping: 0,
    quantity: 0
  };

  if (!analytics.ecommerce?.checkout) {
    return base_values;
  }

  const { checkout } = analytics.ecommerce;
  const quantity = checkout.products[0].quantity;

  return {
    quantity,
    purchase_tax: sum(map(checkout.products, "tax")) * quantity,
    purchase_shipping: sum(map(checkout.products, "shipping")) * quantity
  };
};

export function thankYouPage(
  purchase_id: number | null,
  payment_type: PaymentType,
  billing_country: string,
  analytics: TrackingAnalytics,
  customer_value: {
    amount: number;
    currency: CurrencyCode;
  }
): TrackingPage {
  const details = Object.assign(
    {
      /** GTM Variable "event payment_type" */
      payment_type,
      /** GTM Variable "event purchase_id" */
      purchase_id: purchase_id,
      /** GTM Variable "event billing_country" */
      thanks_billing_country: billing_country,
      /** GTM Variable "event customer_value" */
      customer_value: customer_value.amount / 100,
      /** GTM Variable "event customer_value_currency" */
      customer_value_currency: customer_value.currency
    },
    analytics.page.details
  );

  return {
    name: "thanks",
    path: "/thank-you",
    details: details
  };
}

export function failedOrder(
  payment_type: PaymentType,
  purchase_id: number | null,
  billing_info: Nullable<Pick<BillingInfo, "country" | "city" | "state">>,
  error_name: string,
  passengers: Array<
    Pick<CartPassenger | BookingPassenger, "first_name" | "last_name">
  >,
  analytics: TrackingAnalytics,
  source: ConfirmationSource | undefined | null
): AmplitudeEvent<FailedOrder> {
  const properties = Object.assign(
    {
      /** GTM Variable "event purchase_id" */
      purchase_id: purchase_id,
      checkout_billing_country: billing_info.country,
      checkout_billing_city: billing_info.city,
      checkout_billing_state: billing_info.state,
      checkout_error_name: error_name,
      passengers: passengers.map(p => [p.first_name, p.last_name].join(" ")),
      source: source,
      payment_type
    },
    analytics.page.details
  );

  return {
    action: CheckoutTrackingAction.FailedOrder,
    category: "errors",
    label: properties.purchase_id,
    details: properties
  };
}

export type PassengerInfo = {
  email: string | null;
  first_name: string | null;
  last_name: string | null;
};

export function userWithPurchase(
  user_id: number | null,
  passengers: PassengerInfo[],
  billing_info: Nullable<Pick<BillingInfo, "country" | "city" | "state">>,
  analytics: TrackingAnalytics
): TrackingUser {
  const main_passenger = passengers[0];

  return {
    user_id: user_id,
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    email: main_passenger.email,
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    first_name: main_passenger.first_name,
    // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
    last_name: main_passenger.last_name,
    billing_country: billing_info.country,
    billing_city: billing_info.city,
    billing_state: billing_info.state,
    language: analytics.config.language
  };
}

export function clickedUpdatePrice(
  new_price: number,
  old_price: number,
  operators: string,
  analytics: TrackingAnalytics
): AmplitudeEvent<ClickedUpdatePrice> {
  const properties: { cart_id: string | undefined } = analytics.page.details;
  const price_variation = new_price - old_price;

  return {
    action: CheckoutTrackingAction.ClickedUpdatePrice,
    category: "checkout",
    label: properties.cart_id,
    details: {
      checkout_price_variation: price_variation,
      checkout_price_variation_pc: Math.floor(
        (price_variation / old_price) * 100
      ),
      checkout_operators: operators
    }
  };
}

export function cancelledPriceUpdate(
  new_price: number,
  old_price: number,
  operators: string,
  analytics: TrackingAnalytics
): AmplitudeEvent<CancelledPriceUpdate> {
  const properties: { cart_id: string | undefined } = analytics.page.details;
  const price_variation = new_price - old_price;

  return {
    action: CheckoutTrackingAction.CancelledPriceUpdate,
    category: "checkout",
    label: properties.cart_id,
    details: {
      checkout_price_variation: price_variation,
      checkout_price_variation_pc: Math.floor(
        (price_variation / old_price) * 100
      ),
      checkout_operators: operators
    }
  };
}

export function clickedUpdateCurrency(
  old_currency: string,
  suggested_currency: string,
  operators: NapiOperator[],
  analytics: TrackingAnalytics,
  picker_location: "footer" | "price_breakdown" = "footer"
): AmplitudeEvent<ClickedUpdateCurrency> {
  const properties: { cart_id: string | undefined } = analytics.page.details;

  return {
    action: CheckoutTrackingAction.ClickedUpdateCurrency,
    category: "checkout",
    label: properties.cart_id,
    details: {
      checkout_old_currency: old_currency,
      checkout_suggested_currency: suggested_currency,
      checkout_currency_change: `${old_currency}-${suggested_currency}`,
      checkout_operators: operators
        .map(({ name }: { name: string }) => name)
        .join(", "),
      picker_location
    }
  };
}

export function openedInstallmentsField(): AmplitudeEvent<OpenedInstallmentsField> {
  return {
    action: CheckoutTrackingAction.OpenedInstallmentsField,
    category: "checkout",
    details: {}
  };
}

export function updatedInstallmentsQuantity(
  installments_quantity: number
): AmplitudeEvent<UpdatedInstallmentsQuantity> {
  return {
    action: CheckoutTrackingAction.UpdatedInstallmentsQuantity,
    category: "checkout",
    details: {
      installments_quantity
    }
  };
}

export function clickedRedeemCredits(): AmplitudeEvent<ClickedRedeemCredits> {
  return {
    action: CheckoutTrackingAction.ClickedRedeemCredits,
    details: {}
  };
}

export function redeemCreditsError(
  error_type: string
): AmplitudeEvent<RedeemCreditsError> {
  return {
    action: CheckoutTrackingAction.RedeemCreditsError,
    details: {
      error_type: error_type
    }
  };
}

export function unredeemCreditsError(
  error_type: string
): AmplitudeEvent<UnredeemCreditsError> {
  return {
    action: CheckoutTrackingAction.UnredeemCreditsError,
    details: {
      error_type: error_type
    }
  };
}

export function clickedUnredeemCredits(): AmplitudeEvent<ClickedUnredeemCredits> {
  return {
    action: CheckoutTrackingAction.ClickedUnredeemCredits,
    details: {}
  };
}

export function discountCodeError(
  error_type: string | Error
): AmplitudeEvent<DiscountCodeError> {
  return {
    action: CheckoutTrackingAction.DiscountCodeError,
    details: {
      error_type:
        typeof error_type === "string" ? error_type : error_type.message
    }
  };
}

export function openedCountryPicker(): AmplitudeEvent<OpenedCountryPicker> {
  return {
    action: CheckoutTrackingAction.OpenedCountryPicker,
    details: {}
  };
}

export function pickedCountryInPicker(
  country: GeoDataCountry
): AmplitudeEvent<PickedCountryInPicker> {
  return {
    action: CheckoutTrackingAction.PickedCountryInPicker,
    details: { country }
  };
}

export function pickedCountryCodeInPicker(
  country: string
): AmplitudeEvent<PickedCountryCodeInPicker> {
  return {
    action: CheckoutTrackingAction.PickedCountryInPicker,
    details: { country }
  };
}

export function clickedEmailOptInCheckbox(
  checked: boolean
): AmplitudeEvent<ClickedEmailOptInCheckbox> {
  return {
    action: CheckoutTrackingAction.ClickedEmailOptInCheckbox,
    category: "checkout",
    details: { checked }
  };
}

export function closedSeatSelection(): AmplitudeEvent<ExitedCheckoutSeats> {
  return {
    action: CheckoutTrackingAction.ExitedCheckoutSeats,
    category: "checkout",
    details: {}
  };
}

export function openedSeatSelection(): AmplitudeEvent<OpenedCheckoutSeats> {
  return {
    action: CheckoutTrackingAction.OpenedCheckoutSeats,
    category: "checkout",
    details: {}
  };
}

export function openedRefundProtectDetails(): AmplitudeEvent<OpenedRefundProtectDetails> {
  return {
    action: CheckoutTrackingAction.OpenedRefundProtectDetails,
    category: "checkout",
    details: {}
  };
}

export function clickedSeat(
  seat_number: string
): AmplitudeEvent<ClickedCheckoutSeat> {
  return {
    action: CheckoutTrackingAction.ClickedCheckoutSeat,
    category: "checkout",
    details: { seat_number }
  };
}

export function clickedCreateAccountToggle(): AmplitudeEvent<ClickedCreateAccountToggle> {
  return {
    action: CheckoutTrackingAction.ClickedCreateAccountToggle,
    category: "checkout",
    details: {}
  };
}

function getSelectionAncillaryPayload(ancillary: Ancillary) {
  return {
    ancillary,
    product_type: isHepstarAncillary(ancillary)
      ? ancillary.partner_data.type
      : (ancillary.category as BusbudProductType),
    type: ancillary.ancillary_type
  };
}

export function selectedAncillary(
  ancillary: Ancillary
): AmplitudeEvent<SelectedAncillary> {
  return {
    action: CheckoutTrackingAction.SelectedAncillary,
    category: "checkout",
    details: getSelectionAncillaryPayload(ancillary)
  };
}

export function unselectedAncillary(
  ancillary: Ancillary
): AmplitudeEvent<UnselectedAncillary> {
  return {
    action: CheckoutTrackingAction.UnselectedAncillary,
    category: "checkout",
    details: getSelectionAncillaryPayload(ancillary)
  };
}

export function autofilledTraveller(
  analytics: TrackingAnalytics
): AmplitudeEvent<SelectedSavedTraveller> {
  const properties = analytics.page.details;
  return {
    action: CheckoutTrackingAction.SelectedSavedTraveller,
    category: "checkout",
    details: properties
  };
}

export function nonAlphabeticNameWarningTriggered(): AmplitudeEvent<NonAlphabeticNameWarningTriggered> {
  return {
    action: CheckoutTrackingAction.NonAlphabeticNameWarningTriggered,
    details: {}
  };
}

export function clickedReferralCartSelect(
  direction: "outbound" | "return",
  cart_id: string
): AmplitudeEvent<ClickedReferralCartSelect> {
  return {
    action: CheckoutTrackingAction.ClickedReferralCartSelect,
    category: "referral_checkout",
    label: cart_id,
    details: { direction }
  };
}

export function savePassengerQuestionAnswersCheckboxToggled(
  previous_state: boolean
): AmplitudeEvent<SavePassengerQuestionAnswersCheckboxToggled> {
  const getStatus = (state: boolean) => (state ? "checked" : "unchecked");

  return {
    action: CheckoutTrackingAction.SavePassengerQuestionAnswersCheckboxToggled,
    category: "checkout",
    details: {
      previous_state: getStatus(previous_state),
      new_state: getStatus(!previous_state)
    }
  };
}

export function checkoutAddonsViewed(
  checkout_addons_viewed: AncillaryCategory[]
): AmplitudeEvent<CheckoutAddonsViewed> {
  return {
    action: CheckoutTrackingAction.AddonsViewed,
    category: "checkout",
    details: {
      checkout_addons_viewed
    }
  };
}

export function checkoutAddonsSelected(
  checkout_addons_selected: AncillaryCategory[]
): AmplitudeEvent<CheckoutAddonsSelected> {
  return {
    action: CheckoutTrackingAction.AddonsSelected,
    category: "checkout",
    details: {
      checkout_addons_selected
    }
  };
}

export function checkoutPaymentFormViewed(
  payment_form_viewed: CheckoutPaymentFormViewed
): AmplitudeEvent<CheckoutPaymentFormViewed> {
  return {
    action: CheckoutTrackingAction.PaymentFormViewed,
    category: "checkout",
    details: payment_form_viewed
  };
}

export function checkoutPaymentFormSubmitted(
  payment_form_submit_params: CheckoutPaymentFormSubmitted
): AmplitudeEvent<CheckoutPaymentFormSubmitted> {
  return {
    action: CheckoutTrackingAction.PaymentFormSubmitted,
    category: "checkout",
    details: payment_form_submit_params
  };
}
